import { useQuery } from "@tanstack/react-query"
import { AlertCircle, CalendarX, CircleCheck, HandCoins, Link2, Plus } from "lucide-react"
import { useState } from "react"
import { useNavigate } from "react-router"

import { api } from "@/api/cliente"
import { Exportar } from "@/componentes/exportar/Exportar"
import { AntiguedadCartera } from "@/componentes/finanzas/AntiguedadCartera"
import { CarteraPorTercero, type GrupoCartera } from "@/componentes/finanzas/CarteraPorTercero"
import { documentoDeCartera } from "@/componentes/finanzas/exportarCartera"
import { FichaCxc } from "@/componentes/finanzas/FichaCxc"
import {
  abierta,
  indicadores,
  type Partida,
  vencida,
} from "@/componentes/finanzas/indicadoresCartera"
import { ModalNuevaCxc, todosLosClientes } from "@/componentes/finanzas/ModalNuevaCxc"
import { type FilaSaldo, TablaSaldos } from "@/componentes/finanzas/TablaSaldos"
import { monto } from "@/componentes/tesoreria/dinero"
import { Boton } from "@/componentes/ui/Boton"
import type { Tono } from "@/componentes/ui/Insignia"
import { TarjetaKpi } from "@/componentes/ui/TarjetaKpi"
import { tienePermiso } from "@/lib/autorizacion"
import { hoyLocal } from "@/lib/fechas"
import { usarSesion } from "@/lib/sesion"

type Cxc = {
  id: number
  cliente_id: number
  concepto: string
  monto: string
  monto_cobrado: string
  moneda: "USD" | "BS"
  estado: string
  fecha_emision: string | null
  fecha_vencimiento: string | null
  expediente_numero: string | null
  sistema2_clave: string | null
  /** Si es el espejo de un expediente del 2.0: cómo está allí. Sólo informa. */
  estado_sistema2: "COBRADA" | "ABONADA" | "PENDIENTE" | "ANULADA" | null
  sistema2_total: string | null
  sistema2_cobrado: string | null
  /** (23/10) El paso del cobro del expediente en el 2.0, dentro de «Por cobrar». */
  sistema2_subestatus?: string | null
}

/** (23/10) El sub-estatus de cobro del 2.0, en palabras. */
const SUBESTATUS: Record<string, string> = {
  POR_RECIBIR_FACTURAS: "Por recibir facturas",
  POR_RECIBIR_RETENCIONES: "Por recibir retenciones",
  VALIJA_ENVIADA: "Valija enviada",
  VALIJA_RECIBIDA: "Valija recibida",
}

/**
 * La insignia del espejo del 2.0 (16/10): dice cómo está ALLÍ la cuenta, que se
 * gestiona aparte. No mueve nada de aquí: «Cobrada en el 2.0» con saldo en el 3.0
 * quiere decir que falta registrar aquí el ingreso.
 */
/** Lo que filtra la tabla al pulsar una tarjeta. */
type Filtro = "abiertas" | "vencidas" | "cobradas_2" | null

/** El filtro, dicho en el subtítulo de lo que se exporta. */
const FILTRO_EN_PALABRAS: Record<NonNullable<Filtro> | "todas", string> = {
  todas: "Todas las cuentas por cobrar",
  abiertas: "Sólo las que tienen saldo",
  vencidas: "Sólo las vencidas",
  cobradas_2: "Cobradas en el 2.0 con saldo todavía en el 3.0",
}

const INSIGNIA_2: Record<NonNullable<Cxc["estado_sistema2"]>, { texto: string; tono: Tono }> = {
  COBRADA: { texto: "Cobrada en el 2.0", tono: "exito" },
  ABONADA: { texto: "Abonada en el 2.0", tono: "info" },
  PENDIENTE: { texto: "Pendiente en el 2.0", tono: "aviso" },
  ANULADA: { texto: "Anulada en el 2.0", tono: "peligro" },
}

/**
 * Bandeja de cuentas por cobrar.
 *
 * Una fila saldada no ofrece cobrar, y esa decisión vive en `TablaSaldos` —la misma
 * tabla que usan tesorería y la bandeja de pagar—.
 *
 * ⚠ **Aquí NO se cobra: se cobra desde Tesorería → «Ingresos»** (15/10, pedido del
 * usuario). Hubo un «Cobrar» que registraba el cobro sin el dinero detrás —sin
 * movimiento de banco—, y con el ingreso aplicado además se contaba dos veces. El cobro
 * nace del ingreso que lo paga, en su moneda y con su tasa si la CxC es de la otra
 * (`componentes/ingresos/RepartoCxc.tsx`). El botón de la fila lleva allí.
 */
export function CuentasPorCobrar() {
  const usuario = usarSesion((e) => e.usuario)
  const navegar = useNavigate()
  const [creando, setCreando] = useState(false)
  const [filtro, setFiltro] = useState<Filtro>(null)
  // Agrupada por cliente por omisión, como el 2.0 (16/10); «Lista» es la de siempre.
  const [vista, setVista] = useState<"cliente" | "lista">("cliente")
  // La CxC cuya ficha está abierta (16/10): se abre pulsando el expediente.
  const [enFicha, setEnFicha] = useState<number | null>(null)

  const consulta = useQuery({
    queryKey: ["cxc"],
    queryFn: () => api.get<Cxc[]>("/api/v1/cxc"),
  })

  const clientes = useQuery({
    queryKey: ["clientes", "todos-activos"],
    queryFn: todosLosClientes,
    enabled: tienePermiso(usuario, "cliente:ver"),
  })

  if (consulta.isPending) {
    return <p className="p-6">Cargando…</p>
  }
  if (consulta.isError) {
    return (
      <p className="flex items-center gap-2 p-6" style={{ color: "var(--destructive)" }}>
        <AlertCircle size={16} aria-hidden={true} /> No se pudo cargar la cartera.
      </p>
    )
  }

  // ⚠ El API de clientes dice `razon_social`; aquí se leía `nombre`, que no existe, y
  // la bandeja nunca enseñó a quién se le cobra (15/10).
  const nombreDe = new Map((clientes.data ?? []).map((c) => [c.id, c.razon_social]))
  const hoy = hoyLocal()
  const partida = (f: Cxc): Partida => ({
    monto: f.monto,
    aplicado: f.monto_cobrado,
    moneda: f.moneda,
    vence: f.fecha_vencimiento,
    saldada: f.estado === "COBRADO",
    anulada: f.estado === "ANULADA",
    tercero: f.cliente_id,
  })
  const kpi = indicadores(consulta.data.map(partida), hoy)
  // Cobradas en el 2.0 y con saldo aquí: el ingreso no se ha registrado en el 3.0.
  const cobradas2 = consulta.data.filter(
    (f) => f.estado_sistema2 === "COBRADA" && abierta(partida(f)),
  )
  const kpi2 = indicadores(cobradas2.map(partida), hoy)
  const visibles = consulta.data.filter((f) =>
    filtro === "abiertas"
      ? abierta(partida(f))
      : filtro === "vencidas"
        ? vencida(partida(f), hoy)
        : filtro === "cobradas_2"
          ? cobradas2.includes(f)
          : true,
  )
  const alternar = (f: Filtro) => setFiltro((actual) => (actual === f ? null : f))
  const fila = (f: Cxc, conCliente: boolean): FilaSaldo => ({
    id: f.id,
    // A quién se le cobra, delante: el concepto solo no lo dice. En la vista por
    // cliente sobra, porque ya lo dice la tarjeta.
    concepto:
      conCliente && nombreDe.has(f.cliente_id)
        ? `${nombreDe.get(f.cliente_id)} · ${f.concepto}`
        : f.concepto,
    monto: f.monto,
    aplicado: f.monto_cobrado,
    moneda: f.moneda,
    saldada: f.estado === "COBRADO",
    anulada: f.estado === "ANULADA",
    insignia: f.estado_sistema2
      ? {
          // Abierta allí y con sub-estatus: el paso del cobro dice más que «pendiente».
          ...(f.sistema2_subestatus &&
          SUBESTATUS[f.sistema2_subestatus] &&
          f.estado_sistema2 !== "COBRADA" &&
          f.estado_sistema2 !== "ANULADA"
            ? { texto: `${SUBESTATUS[f.sistema2_subestatus]} (2.0)`, tono: "info" as Tono }
            : INSIGNIA_2[f.estado_sistema2]),
          titulo: `En el 2.0: ${monto(f.sistema2_cobrado ?? "0")} cobrados de ${monto(f.sistema2_total ?? "0")} ${f.moneda}`,
        }
      : undefined,
  })
  const filas = visibles.map((f) => fila(f, true))
  const grupos: GrupoCartera[] = [
    ...visibles
      .reduce((acc, f) => {
        const g = acc.get(f.cliente_id) ?? {
          id: f.cliente_id,
          nombre: nombreDe.get(f.cliente_id) ?? `Cliente ${f.cliente_id}`,
          filas: [],
          partidas: [],
        }
        g.filas.push(fila(f, false))
        g.partidas.push(partida(f))
        return acc.set(f.cliente_id, g)
      }, new Map<number, GrupoCartera>())
      .values(),
  ]

  const ficha = consulta.data.find((f) => f.id === enFicha) ?? null
  const puedeIngresos = tienePermiso(usuario, "ingreso:registrar")

  const subtitulo = [
    FILTRO_EN_PALABRAS[filtro ?? "todas"],
    vista === "cliente" ? "ordenadas por cliente" : null,
  ]
    .filter(Boolean)
    .join(", ")
  const datos = consulta.data
  const documento = () => {
    const tercero = (f: Cxc) => nombreDe.get(f.cliente_id) ?? `Cliente ${f.cliente_id}`
    const orden =
      vista === "cliente"
        ? [...visibles].sort((a, b) => tercero(a).localeCompare(tercero(b), "es"))
        : visibles
    return documentoDeCartera({
      titulo: "Cuentas por cobrar",
      subtitulo,
      etiquetaTercero: "Cliente",
      etiquetaReferencia: "Expediente",
      etiquetaAplicado: "Cobrado",
      conEmision: true,
      filas: orden.map((f) => ({
        tercero: tercero(f),
        concepto: f.concepto,
        referencia: f.expediente_numero ?? f.sistema2_clave,
        emision: f.fecha_emision,
        vence: f.fecha_vencimiento,
        estado: f.estado,
        estado2: f.estado_sistema2
          ? f.sistema2_subestatus &&
            SUBESTATUS[f.sistema2_subestatus] &&
            f.estado_sistema2 !== "COBRADA" &&
            f.estado_sistema2 !== "ANULADA"
            ? `${SUBESTATUS[f.sistema2_subestatus]} (2.0)`
            : INSIGNIA_2[f.estado_sistema2].texto
          : null,
        moneda: f.moneda,
        monto: f.monto,
        aplicado: f.monto_cobrado,
      })),
      antiguedadDe: {
        titulo: "Antigüedad de las cuentas por cobrar",
        partidas: datos.map(partida),
        hoy,
      },
    })
  }

  return (
    <section className="space-y-4 p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
          Se cobran desde Tesorería → Ingresos: el ingreso del cliente se aplica a sus cuentas,
          también de la otra moneda con su tasa.
        </p>
        <div className="flex flex-wrap items-start gap-2">
          {visibles.length > 0 && (
            <Exportar titulo="Cuentas por cobrar" subtitulo={subtitulo} documento={documento} />
          )}
          {tienePermiso(usuario, "cxc:cobrar") && (
            <Boton variante="primario" onClick={() => setCreando(true)}>
              <Plus size={16} aria-hidden={true} /> Nueva cuenta por cobrar
            </Boton>
          )}
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <TarjetaKpi
          titulo="Por cobrar"
          detalle="Lo que falta, cuenta por cuenta"
          Icono={HandCoins}
          color="var(--primary)"
          cantidad={kpi.abiertas.cuentas}
          montos={kpi.abiertas.montos}
          pie={`De ${kpi.terceros} ${kpi.terceros === 1 ? "cliente" : "clientes"}`}
          activa={filtro === "abiertas"}
          onClick={() => alternar("abiertas")}
        />
        <TarjetaKpi
          titulo="Vencidas"
          detalle="Con saldo y la fecha ya pasada"
          Icono={CalendarX}
          color="var(--destructive)"
          cantidad={kpi.vencidas.cuentas}
          montos={kpi.vencidas.montos}
          activa={filtro === "vencidas"}
          onClick={() => alternar("vencidas")}
        />
        <TarjetaKpi
          titulo="Cobrado"
          detalle="Lo aplicado con ingresos en el 3.0"
          Icono={CircleCheck}
          color="var(--success)"
          cantidad={kpi.aplicado.cuentas}
          montos={kpi.aplicado.montos}
          pie={kpi.aplicado.cuentas === 1 ? "cuenta saldada" : "cuentas saldadas"}
        />
        <TarjetaKpi
          titulo="Cobradas en el 2.0"
          detalle="Con saldo todavía en el 3.0"
          Icono={Link2}
          color="var(--warning)"
          cantidad={kpi2.abiertas.cuentas}
          montos={kpi2.abiertas.montos}
          pie="Falta registrar aquí su ingreso"
          activa={filtro === "cobradas_2"}
          onClick={() => alternar("cobradas_2")}
        />
      </div>

      <AntiguedadCartera
        partidas={consulta.data.map(partida)}
        hoy={hoy}
        titulo="Antigüedad de las cuentas por cobrar"
      />

      <div className="flex justify-end">
        <fieldset className="inline-flex gap-1 rounded-[var(--radius-control)] p-1 text-sm">
          <legend className="sr-only">Cómo ver la cartera</legend>
          {(
            [
              ["cliente", "Por cliente"],
              ["lista", "Lista"],
            ] as const
          ).map(([valor, etiqueta]) => (
            <label
              key={valor}
              className={`foco-cristal cursor-pointer rounded-[var(--radius-control)] px-3 py-1 ${vista === valor ? "activo-cristal" : "toque-cristal"}`}
            >
              <input
                type="radio"
                name="vista-cxc"
                value={valor}
                checked={vista === valor}
                onChange={() => setVista(valor)}
                className="sr-only"
              />
              {etiqueta}
            </label>
          ))}
        </fieldset>
      </div>

      {vista === "cliente" ? (
        <CarteraPorTercero
          grupos={grupos}
          hoy={hoy}
          etiquetaAplicado="Cobrado"
          accion="Cobrar con un ingreso"
          onAccion={puedeIngresos ? () => navegar("/tesoreria/ingresos") : undefined}
          onAbrir={setEnFicha}
        />
      ) : (
        <TablaSaldos
          filas={filas}
          etiquetaAplicado="Cobrado"
          accion="Cobrar con un ingreso"
          onAccion={puedeIngresos ? () => navegar("/tesoreria/ingresos") : undefined}
          onAbrir={setEnFicha}
        />
      )}

      {ficha && (
        <FichaCxc
          cxc={{
            ...ficha,
            cliente: nombreDe.get(ficha.cliente_id) ?? `Cliente ${ficha.cliente_id}`,
            insignia: ficha.estado_sistema2 ? INSIGNIA_2[ficha.estado_sistema2] : undefined,
          }}
          onCerrar={() => setEnFicha(null)}
        />
      )}

      {creando && <ModalNuevaCxc onCerrar={() => setCreando(false)} />}
    </section>
  )
}
